import { useTranslation } from '@rhino-project/core/i18n';
import { TextField, Input, Label, CloseButton } from '@heroui/react';

import {
  FieldValues,
  Path,
  useController,
  UseControllerProps,
  useFormContext
} from 'react-hook-form';
import React, { ReactNode, useCallback } from 'react';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type FilterInputProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  /**
   * The path inside the form object.
   */
  path: Path<T>;
  label?: ReactNode;
  placeholder?: string;
  type?: string;
  className?: string;
  isClearable?: boolean;
  [key: string]: unknown;
};

export const FilterInputBase = <T extends FieldValues = FieldValues>({
  path,
  rules,
  shouldUnregister,
  defaultValue,
  disabled,
  label,
  placeholder,
  type,
  className,
  isClearable = false,
  ...props
}: FilterInputProps<T>) => {
  const { t } = useTranslation();
  const {
    field: { onChange, value, ...fieldProps }
  } = useController({
    name: path,
    rules,
    shouldUnregister,
    defaultValue,
    disabled
  });
  const { resetField } = useFormContext();

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      ({ target }) => onChange(target.value === '' ? null : target.value),
      [onChange]
    );

  return (
    <TextField
      {...fieldProps}
      value={value == null ? '' : value}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <div className="flex">
        <Input
          placeholder={placeholder}
          type={type}
          onChange={handleOnChange}
        />
        {isClearable && value && (
          <CloseButton
            aria-label={t('rhino.frontend.messages.clear')}
            onPress={() => resetField(path)}
          />
        )}
      </div>
    </TextField>
  );
};

export const FilterInput = (props: FilterInputProps) =>
  useGlobalComponent('FilterInput', FilterInputBase, props);
